import { useEffect, useState } from 'react';
import type { GrowthPayment, GrowthPerson, PersonUsage } from '@/lib/admin-data';
import { NOT_MEASURED, ago, day, money, num, plural, when } from '@/lib/format';
import { OutreachComposer } from './OutreachComposer';
import s from '@/pages/console/console.module.css';

export const STATE_LABEL: Record<GrowthPerson['state'], string> = {
  paid: 'Paid',
  abandoned_checkout: 'Abandoned checkout',
  checked: 'Did a check',
  signed_up: 'Signed up, no check yet',
  unverified: 'Not verified yet',
};

/**
 * One line under the name: when they signed up, how they have used the
 * product, and where they asked for something. Counts only, each measured;
 * the detail and the Journal are one click further.
 */
export const PAID_SOURCE_LABEL: Record<NonNullable<GrowthPayment['paidSource']>, string> = {
  invoice: 'Stripe invoice',
  checkout_session: 'Stripe checkout',
  customer_invoice: 'Stripe customer invoice',
};

/** "paid $59.99" from the payment row, or the words when Stripe showed nothing. */
export const paidLabel = (payment: GrowthPayment | null | undefined): string | null => {
  if (!payment) return null;
  if (payment.paidUsd === null) return `paid amount ${NOT_MEASURED}`;
  return `paid ${money(payment.paidUsd)}${payment.coupon ? ` with ${payment.coupon}` : ''}`;
};

export function UsageRow({ p, usage, payment }: { p: GrowthPerson; usage: PersonUsage | null | undefined; payment?: GrowthPayment | null }) {
  const asks = usage ? usage.asks.supportTickets.count + usage.asks.helpRequests.count + usage.asks.elinaMessages.count + usage.asks.reflections.count : null;
  const paid = paidLabel(payment);
  return (
    <div className={s.usageRow}>
      <span>signed up <strong>{day(p.signupAt ?? p.createdAt)}</strong></span>
      {paid && <span><strong>{paid}</strong>{payment?.paidAt ? ` ${day(payment.paidAt)}` : ''}</span>}
      <span><strong>{plural(p.checks, 'check day')}</strong>{p.checks > 0 ? `, last ${ago(p.lastCheckAt)}` : ''}</span>
      {usage === undefined && <span className={s.muted}>usage loading</span>}
      {usage === null && <span className={s.muted}>usage {NOT_MEASURED}</span>}
      {usage && (
        <>
          <span>Active Mode <strong>{usage.active.sessions > 0 ? (usage.active.minutes === null ? `${plural(usage.active.sessions, 'session')}, minutes ${NOT_MEASURED}` : `${num(usage.active.minutes)} min`) : 'none'}</strong></span>
          <span>exercises <strong>{num(usage.exercises.count)}</strong></span>
          <span>ROM <strong>{num(usage.rom.count)}</strong></span>
          <span className={asks ? s.usageAsk : undefined}>asked <strong>{asks === null ? NOT_MEASURED : asks === 0 ? 'nothing yet' : `${asks} ${asks === 1 ? 'time' : 'times'}, last ${ago(usage.asks.lastAt)}`}</strong></span>
        </>
      )}
    </div>
  );
}

export function PersonCard({ p, usage, payment, open, onSent, onJournal, why }: {
  p: GrowthPerson;
  usage: PersonUsage | null | undefined;
  /** Their most recent payment row, when they have one. */
  payment?: GrowthPayment | null;
  open: boolean;
  onSent: () => void;
  onJournal: (userId: string) => void;
  /** One line on why this person is on the list right now (Today only). */
  why?: string;
}) {
  const [expanded, setExpanded] = useState(open);
  useEffect(() => {
    if (open) document.getElementById(`person-${p.userId}`)?.scrollIntoView({ block: 'center' });
  }, [open, p.userId]);

  return (
    <div id={`person-${p.userId}`} className={`${s.person} ${open ? s.personFocus : ''}`}>
      <div className={s.personTop}>
        <button type="button" className={s.personHead} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          <span className={`${s.pill} ${s[`pill_${p.state}`] ?? ''}`}>{STATE_LABEL[p.state]}</span>
          <span className={s.personName}>{p.name || p.email}</span>
          <span className={s.sub}>{p.email}{p.phone ? ` · ${p.phone}` : ''}</span>
          <span className={s.personMeta}>{p.plan ? `${p.plan}${p.interval ? ` ${p.interval}` : ''}` : 'free'}{p.ghlUrl ? '' : ' · not in GHL yet'}</span>
          <span className={s.chevron}>{expanded ? '▴' : '▾'}</span>
        </button>
        {p.ghlUrl && <a className={s.btnGhost} href={p.ghlUrl} target="_blank" rel="noreferrer">GHL</a>}
        <button type="button" className={s.btnGhost} onClick={() => onJournal(p.userId)} title="Everything this person did, and what Elina said">Journal</button>
      </div>
      <UsageRow p={p} usage={usage} payment={payment} />
      {why && <div className={s.usageRow}><span className={s.usageAsk}>{why}</span></div>}
      {expanded && (
        <div className={s.personBody}>
          <dl className={s.facts}>
            <dt>Verified</dt><dd>{p.verified ? 'yes' : 'no'}</dd>
            <dt>Signed up</dt><dd>{when(p.signupAt ?? p.createdAt)}</dd>
            <dt>First check</dt><dd>{when(p.firstCheckAt, 'no check yet')}</dd>
            <dt>Last check</dt><dd>{when(p.lastCheckAt)}{p.lastScore !== null ? ` · score ${Math.round(p.lastScore)}` : p.checks > 0 ? ` · score ${NOT_MEASURED}` : ''}</dd>
            <dt>Plan</dt><dd>{p.plan ? `${p.plan}${p.interval ? ` · ${p.interval}` : ''} (${p.status})` : 'free'}</dd>
            {payment && (
              <><dt>Paid</dt><dd>{payment.paidUsd === null ? `${NOT_MEASURED} (Stripe showed no payment)` : `${money(payment.paidUsd)} on ${when(payment.paidAt)}, from the ${payment.paidSource ? PAID_SOURCE_LABEL[payment.paidSource] : 'unknown Stripe object'}`}{payment.priceUsd !== null ? ` · list ${money(payment.priceUsd)}` : ''}{payment.coupon ? ` · code ${payment.coupon}` : ''} · renews {when(payment.periodEnd)}</dd></>
            )}
            {p.checkoutOpenedAt && <><dt>Checkout opened</dt><dd>{when(p.checkoutOpenedAt)}</dd></>}
            <dt>Came from</dt><dd>{[p.source, p.medium, p.campaign].filter(Boolean).join(' / ') || 'not captured'}{p.promoterCode ? ` · code ${p.promoterCode}` : ''}</dd>
            <dt>Journey sent</dt><dd>{p.journey.length ? p.journey.join(', ') : 'nothing yet'}</dd>
            <dt>Reports</dt><dd>{p.feedbackCount}</dd>
            <dt>Timezone</dt><dd>{p.timezone ?? 'unknown'}</dd>
            <dt>GHL</dt><dd>{p.ghlUrl ? <a href={p.ghlUrl} target="_blank" rel="noreferrer">open contact</a> : 'not synced yet'}</dd>
            {usage && (
              <>
                <dt>Active Mode</dt><dd>{usage.active.sessions > 0 ? `${plural(usage.active.sessions, 'session')}, ${usage.active.minutes === null ? `minutes ${NOT_MEASURED}` : `${num(usage.active.minutes)} monitored minutes`}, last ${when(usage.active.lastAt)}` : 'none'}</dd>
                <dt>Exercises</dt><dd>{usage.exercises.count > 0 ? `${num(usage.exercises.count)}, last ${when(usage.exercises.lastAt)}` : 'none'}</dd>
                <dt>ROM</dt><dd>{usage.rom.count > 0 ? `${num(usage.rom.count)}, last ${when(usage.rom.lastAt)}` : 'none'}</dd>
                <dt>Support tickets</dt><dd>{usage.asks.supportTickets.count > 0 ? `${usage.asks.supportTickets.count}, last ${when(usage.asks.supportTickets.lastAt)}` : 'none'}</dd>
                <dt>Help requests</dt><dd>{usage.asks.helpRequests.count > 0 ? `${usage.asks.helpRequests.count}, last ${when(usage.asks.helpRequests.lastAt)}` : 'none'}</dd>
                <dt>Elina messages</dt><dd>{usage.asks.elinaMessages.count > 0 ? `${usage.asks.elinaMessages.count} written, last ${when(usage.asks.elinaMessages.lastAt)}` : 'none'}</dd>
                <dt>Reflections</dt><dd>{usage.asks.reflections.count > 0 ? `${usage.asks.reflections.count}, last ${when(usage.asks.reflections.lastAt)}` : 'none'}</dd>
              </>
            )}
            {p.outreach.length > 0 && (
              <><dt>Notes sent</dt><dd>{p.outreach.map((o) => `${o.channel} ${o.status} ${when(o.at)}`).join('; ')}</dd></>
            )}
          </dl>
          <OutreachComposer p={p} onSent={onSent} />
        </div>
      )}
    </div>
  );
}
